class ApiKeyWindow extends HTMLElement {
    static createId = Ext.data.SequentialIdGenerator.create({
        type: 'sequential',
        prefix: 'ApiKeyWindow-',
        seed: 1000
    });

    static defaults = {
        generateButtonText: 'Generate',
        regenerateButtonText: 'Regenerate Key'
    };

    #instanceId = '';
    #keyFieldInstructions = '';

    constructor() {
        super();

        this.#instanceId = this.constructor.createId.generate();
    }

    connectedCallback() {
        this.#render();

        this.dialog = this.querySelector('dialog');
        this.alertEl = this.querySelector('.sa-api-key-window-alert');
        this.generatorEl = this.querySelector('.sa-api-key-window-generator');
        this.generateBtn = this.querySelector('.sa-api-key-generate-btn');
        this.keyField = this.querySelector('.sa-api-key-field');
        this.copyBtn = this.querySelector('.sa-api-key-window-copy-btn');

        this.#keyFieldInstructions = this.keyField.textContent;

        this.generateBtn.addEventListener('click', this);
        this.dialog.addEventListener('close', this);
    }

    handleEvent(event) {
        if (event.type === 'click') {
            this.#generateApiKey();
        } else if (event.type === 'close') {
            this.#reset();
        }
    }

    async #generateApiKey() {
        this.generateBtn.disabled = true;

        try {
            const { key, expires } = await SA.Ajax.request({
                method: 'regenerate',
                resource: 'user/authenticationKey'
            });

            this.keyField.textContent = key;

            this.generateBtn.textContent = this.constructor.defaults.regenerateButtonText;

            this.copyBtn.disabled = false;

            this.showAlert(expires);
        } catch (err) {
            SA.error(err);
        } finally {
            this.generateBtn.disabled = false;
        }
    }

    open() {
        this.dialog.showModal();
    }

    #render() {
        const targetId = `${this.#instanceId}-api-key`;
        const titleId = `${this.#instanceId}-api-key-generate-title`;
        const descriptionId = `${this.#instanceId}-api-key-generate-instructions`;
        const template = document.createElement('template');

        template.innerHTML = `
            <dialog
                aria-labelledby="${titleId}"
                aria-describedby="${descriptionId}"
                class="sa-api-key-window"
            >
                <form method="dialog">
                    <button aria-label="close modal" class="sa-close">
                        <svg aria-hidden="true"><use href="#sa-icon-close"></use></svg>
                    </button>
                </form>
                <header class="sa-api-key-window-heading">
                    <h3 id="${titleId}" class="sa-api-key-window-title" tabindex="-1">
                        Generate Key
                    </h3>
                </header>
                <div class="sa-api-key-window-body">
                    <div class="sa-api-key-window-content">
                        <p id="${descriptionId}">Generate a key to use with the SimplyAnalytics API.</p>
                        <div hidden role="alert" class="sa-api-key-window-alert">
                            Store this key in a safe place, once this window is closed you will not be able to view it again.<br/>
                            Key expires at <strong><time></time></strong>. You can generate a new key at any time.
                        </div>
                        <div class="sa-api-key-window-generator">
                            <button
                                class="sa-button x-unselectable sa-button-primary sa-api-key-generate-btn"
                                type="button"
                            >
                                <span class="sa-button-text">Generate Key</span>
                            </button>
                            <code class="x-form-field x-form-text sa-api-key-field" id="${targetId}">
                                Copy API key from here after generating
                            </code>
                            <sa-copy-button
                                target-id="${targetId}"
                                success-text="API key copied"
                                error-text="Unable to copy API key."
                            >
                                <button
                                    disabled
                                    aria-label="Copy API key"
                                    class="sa-button x-unselectable sa-button-primary sa-api-key-window-copy-btn"
                                    type="button"
                                >
                                    <svg class="sa-button-svg"><use href="#sa-icon-copy"></use></svg>
                                    <span class="sa-button-text">Copy</span>
                                </button>
                            </sa-copy-button>
                        </div>
                    </div>
                </div>
            </dialog>
        `;

        this.append(template.content.cloneNode(true));
    }

    #reset() {
        this.generateBtn.textContent = this.constructor.defaults.generateButtonText;

        this.keyField.textContent = this.#keyFieldInstructions;

        this.alertEl.querySelector('time').textContent = '';
        this.alertEl.hidden = true;

        this.copyBtn.disabled = true;
    }

    showAlert(expires) {
        const expiryDate = new Date(expires * 1000);
        const expiryText = expiryDate.toLocaleString(SA.Institution.getBcp47Tag());

        this.alertEl.querySelector('time').textContent = expiryText;
        this.alertEl.hidden = false;
    }
}

customElements.define('sa-api-key-window', ApiKeyWindow);
